// The branded confirm dialog, in place of window.confirm.
//
// Turbo calls this for every data-turbo-confirm on the page, so no caller has to know the
// modal exists. Callers that want more than a message can set data-turbo-confirm-title
// and data-turbo-confirm-label on the same element.
//
// Markup: app/views/application/_confirm_modal.html.erb
import { Turbo } from "@hotwired/turbo-rails"

// The wording rendered into the markup, kept because the dialog is reused: a caller that
// sets its own label would otherwise leave it on the button for every confirm after it.
let rendered = null

function modalParts() {
  const root = document.getElementById("confirm-modal")
  if (!root) return null

  const parts = {
    root,
    title: root.querySelector("[data-confirm-title]"),
    message: root.querySelector("[data-confirm-message]"),
    accept: root.querySelector("[data-confirm-accept]"),
    cancel: root.querySelector("[data-confirm-cancel]")
  }

  if (!Object.values(parts).every(Boolean)) return null

  rendered ||= { title: parts.title.textContent, accept: parts.accept.textContent }
  return parts
}

// data-turbo-confirm can sit on the form (button_to) or the link itself, and the submitter
// is the more specific of the two when both are present.
function option(name, submitter, element) {
  return submitter?.dataset?.[name] || element?.dataset?.[name] || null
}

function open(parts, { title, message, label }) {
  // Wording only. The buttons keep the classes they were rendered with, which are the
  // app's own — a confirm that restyles itself is a confirm that stops matching the page
  // it interrupts.
  parts.title.textContent = title || rendered.title
  parts.message.textContent = message
  parts.accept.textContent = label || rendered.accept

  parts.root.classList.remove("hidden")
  parts.root.classList.add("flex")
  document.body.classList.add("overflow-hidden")
  parts.accept.focus()
}

function close(parts) {
  parts.root.classList.add("hidden")
  parts.root.classList.remove("flex")
  document.body.classList.remove("overflow-hidden")
}

const confirmMethod = (message, element, submitter) => {
  const parts = modalParts()
  // No modal on this page — a bare layout, a mailer preview. Fall back rather than let a
  // destructive action through unasked.
  if (!parts) return Promise.resolve(window.confirm(message))

  const restoreFocusTo = document.activeElement

  return new Promise((resolve) => {
    const listeners = new AbortController()
    const finish = (confirmed) => {
      listeners.abort()
      close(parts)
      if (restoreFocusTo instanceof HTMLElement) restoreFocusTo.focus()
      resolve(confirmed)
    }

    const on = (target, event, handler) =>
      target.addEventListener(event, handler, { signal: listeners.signal })

    on(parts.accept, "click", () => finish(true))
    on(parts.cancel, "click", () => finish(false))
    on(parts.root, "click", (event) => {
      if (event.target === parts.root) finish(false)
    })
    on(document, "keydown", (event) => {
      if (event.key === "Escape") return finish(false)
      if (event.key !== "Tab") return

      // Two focusable things in here, so the trap is just a cycle between them. Without it
      // Tab walks off into the page behind the sheet, which on a phone is invisible.
      event.preventDefault()
      const next = document.activeElement === parts.accept ? parts.cancel : parts.accept
      next.focus()
    })

    open(parts, {
      title: option("turboConfirmTitle", submitter, element),
      message: message,
      label: option("turboConfirmLabel", submitter, element)
    })
  })
}

// setConfirmMethod is deprecated in Turbo 8 and logs a warning every load; the config is
// the current home for it. Both, because which one a given Turbo answers to is a detail of
// the version installed, not something worth breaking a delete over.
if (Turbo.config?.forms) {
  Turbo.config.forms.confirm = confirmMethod
} else {
  Turbo.setConfirmMethod(confirmMethod)
}
